import clsx from "clsx"
import { createPortal } from "react-dom"
import { formatPhoneNumber } from "react-phone-number-input"

import useIsDesktop from "@hooks/useIsDesktop"
import useRouter from "@hooks/useRouter"

import { faIcon } from "@utils/faIcon"

import { Button } from "@atoms/index"

import OTPInput from "@molecules/Form/inputs/OTPInput/OTPInput"
import PopoverFooter from "@molecules/Popover/PopoverFooter/PopoverFooter"
import { AlertBanner } from "@molecules/index"

import { useComponentNavigation } from "@organisms/ComponentNavigation/ComponentNavigation.context"
import { OTPForm } from "@organisms/index"

import useAuth from "@pages/Auth/Auth.store"

import { AUTH_ENDPOINTS } from "@endpoints/auth"

import styles from "./AuthOTP.module.scss"
import { AuthOTPProps } from "./AuthOTP.types"

const body = document.querySelector("body")

export default function AuthOTP(props: AuthOTPProps) {
    const { isActive } = props

    const router = useRouter()

    const { preferredAuthMethod, emailData, phoneData, postAuthRedirectRoute } = useAuth()
    const { navigate, currentComponentId } = useComponentNavigation()
    const isDesktop = useIsDesktop()

    const onCodeVerificationSuccessful = () => {
        router.replace(postAuthRedirectRoute)
    }

    const isOnOTPSlide = currentComponentId?.endsWith("_otp")

    const onBackButtonClick = () => {
        if (preferredAuthMethod === "email_and_code") {
            navigate("email_and_code")
        } else if (preferredAuthMethod === "phone_and_code") {
            navigate("phone_and_code")
        }
    }

    return (
        <OTPForm.Root
            checkFor={preferredAuthMethod === "email_and_code" ? "email" : "phone"}
            codeRequestEndpoint={
                preferredAuthMethod === "email_and_code"
                    ? AUTH_ENDPOINTS.OTP.EMAIL_REQUEST
                    : AUTH_ENDPOINTS.OTP.SMS_REQUEST
            }
            codeVerificationEndpoint={
                preferredAuthMethod === "email_and_code"
                    ? AUTH_ENDPOINTS.OTP.EMAIL_AUTHENTICATE
                    : AUTH_ENDPOINTS.OTP.SMS_AUTHENTICATE
            }
            onCodeVerificationSuccessful={onCodeVerificationSuccessful}
            email={emailData?.email ?? undefined}
            emailId={emailData?.emailId ?? undefined}
            phone={phoneData?.phone ?? undefined}
            phoneId={phoneData?.phoneId ?? undefined}
            triggerOTPReset={!isActive}
            hasRequestedCode={true}
        >
            <div className={styles.contentAndForm}>
                <div className={styles.copyWithForm}>
                    <OTPForm.AlertBanner fullWidth={false} type="card" className={styles.alertBanner} />
                    <div className={styles.copy}>
                        <div className={styles.subtitle}>
                            {preferredAuthMethod === "email_and_code" ? (
                                <>
                                    Enter the code we just sent to
                                    <br />
                                    <span className={styles.method}>{emailData?.email}</span>
                                </>
                            ) : (
                                <>
                                    Enter the code we just sent to
                                    <br />
                                    <span className={styles.method}>
                                        {phoneData.phone !== null && formatPhoneNumber(phoneData.phone)}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>

                    <div className={styles.otpInputWrapper}>
                        <OTPInput isFocused={isActive} isTabbable={isActive} />
                    </div>
                    {preferredAuthMethod === "email_and_code" && (
                        <AlertBanner
                            message="Check your inbox and spam folders."
                            status="info"
                            type="tip"
                            title="Tip:"
                            className={styles.tipBanner}
                        />
                    )}
                </div>
                {isDesktop && (
                    <div className={styles.desktopFooter}>
                        <Button
                            onClick={onBackButtonClick}
                            size="lg"
                            variant="ghost"
                            leftIcon={faIcon({ name: "arrow-left", style: "regular" })}
                            isTabbable={isOnOTPSlide}
                        >
                            Back
                        </Button>
                        <OTPForm.SendCodeButton isFullWidth={false} isTabbable={isActive} />
                    </div>
                )}
            </div>
            {!isDesktop &&
                body &&
                createPortal(
                    <PopoverFooter.Root containerClassName={styles.popoverFooter}>
                        <PopoverFooter.LeftSide>
                            <Button
                                onClick={onBackButtonClick}
                                size="lg"
                                variant="ghost"
                                leftIcon={faIcon({ name: "arrow-left", style: "regular" })}
                                className={clsx(styles.backButton, {
                                    [styles.showBackButton]: isOnOTPSlide,
                                })}
                                isTabbable={isOnOTPSlide}
                            >
                                Back
                            </Button>
                        </PopoverFooter.LeftSide>
                        <PopoverFooter.RightSide>
                            <OTPForm.SendCodeButton isTabbable={isActive} />
                        </PopoverFooter.RightSide>
                    </PopoverFooter.Root>,
                    body,
                )}
        </OTPForm.Root>
    )
}
